import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckoutIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { CartSummaryWithDiscounts } from '../components/CartSummaryWithDiscounts';
import { useOrders } from '../hooks/useOrders';
import { useApiCart } from '../hooks/useApiCartProvider';
import { trackEvent } from '../services/analytics';
import { shipToAddressService, ShipToAddress } from '../services/shipToAddressService';
import { useCustomerCreditHold } from '../hooks/useCustomerCreditHold';
import CreditHoldDialog from '../components/CreditHoldDialog';
import { useTranslation } from 'react-i18next';

const CartPage: React.FC = () => {
  const { t } = useTranslation();
  const { createOrderFromCart } = useOrders();
  const { clearCart } = useApiCart();
  const { creditHold } = useCustomerCreditHold();

  // GA4 standard e-commerce event -- fires once per mount so the
  // cart-view step of the funnel is measurable. Add_to_cart already
  // fires when items are added; view_cart marks the deliberate
  // navigation to review the cart before checkout.
  useEffect(() => {
    trackEvent('view_cart');
  }, []);
  const [creditHoldDialogOpen, setCreditHoldDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  
  // Ship-to address state
  const [shipToAddresses, setShipToAddresses] = useState<ShipToAddress[]>([]);
  const [selectedShipToId, setSelectedShipToId] = useState<string>('');
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);

  // Load ship-to addresses when opening checkout
  const handleCheckout = async () => {
    // Intercept on credit hold — surface the notification dialog instead
    // of opening the checkout flow. Backend will also 403 the order
    // creation, but blocking here saves a round trip and matches the spec.
    if (creditHold) {
      setCreditHoldDialogOpen(true);
      return;
    }
    // GA4 standard e-commerce event name -- unlocks the built-in
    // Monetization report's checkout funnel. Pairs with add_to_cart
    // (earlier) and purchase (in useOrders) so drop-off is visible.
    trackEvent('begin_checkout');
    setCheckoutDialogOpen(true);
    await loadShipToAddresses();
  };

  // Load ship-to addresses
  const loadShipToAddresses = async () => {
    try {
      setLoadingAddresses(true);
      setAddressError(null);
      const response = await shipToAddressService.getShipToAddresses(true);
      setShipToAddresses(response.addresses);
    } catch (error: any) {
      setAddressError(error.response?.data?.detail || 'Failed to load ship-to addresses');
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleCloseCheckout = () => {
    setCheckoutDialogOpen(false);
    setNotes('');
    setSelectedShipToId('');
    setShipToAddresses([]);
    setAddressError(null);
  };

  const handleCreateOrder = async () => {
    if (!selectedShipToId) {
      setAddressError('Please select a ship-to address');
      return;
    }

    setIsProcessing(true);
    
    try {
      const order = await createOrderFromCart({
        notes: notes || undefined,
        ship_to_address_id: selectedShipToId,
      });
      
      if (order) {
        setOrderNumber(order.order_number);
        setOrderSuccess(true);
        setCheckoutDialogOpen(false);
        // Clear the cart after successful order
        await clearCart();
      }
    } catch (error: any) {
      // Backend rejects with structured 403 when customer flipped to
      // credit hold between checkout-dialog open and submit. Surface
      // the same notification dialog.
      const detail = error?.response?.data?.detail;
      if (
        error?.response?.status === 403 &&
        ((typeof detail === 'object' && detail?.error_code === 'CREDIT_HOLD') ||
          (typeof detail === 'string' && detail.toLowerCase().includes('credit')))
      ) {
        setCheckoutDialogOpen(false);
        setCreditHoldDialogOpen(true);
      } else {
        console.error('Failed to create order:', error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseSuccess = () => {
    setOrderSuccess(false);
    setOrderNumber('');
    setNotes('');
    setSelectedShipToId('');
    setShipToAddresses([]);
    setAddressError(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ShoppingCartIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          {t('cart.title')}
        </Typography>
      </Box>

      <CartSummaryWithDiscounts onCheckout={handleCheckout} />

      {/* Checkout Dialog */}
      <Dialog
        open={checkoutDialogOpen}
        onClose={handleCloseCheckout}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('cart.checkout')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Ship-to Address Selection */}
            <Grid size={{ xs: 12 }}>
              {loadingAddresses ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : addressError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {addressError}
                </Alert>
              ) : shipToAddresses.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('cart.noShipTo')}
                </Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="ship-to-select-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" />
                      {t('cart.shipToAddress')}
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="ship-to-select-label"
                    value={selectedShipToId}
                    onChange={(e) => setSelectedShipToId(e.target.value as string)}
                    label={t('cart.shipToAddress')}
                    required
                    // Force a tall row so the multi-line address actually
                    // renders in the closed (selected) state — the default
                    // single-line Select would clip everything after the name.
                    sx={{
                      '& .MuiSelect-select': {
                        whiteSpace: 'normal',
                        py: 1.25,
                      },
                    }}
                    // Selected-state renderer: show the address-code chip and
                    // the city line alongside the name so the user can tell
                    // four "Abadi Baru,TK" entries apart at a glance. Without
                    // this, MUI just dumps the MenuItem children which look
                    // cramped on one line.
                    renderValue={(value) => {
                      const a = shipToAddresses.find((x) => x.id === value);
                      if (!a) return '';
                      const locality = [a.city, a.state].filter(Boolean).join(', ');
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {a.name}
                            </Typography>
                            <Chip
                              label={`#${a.epicor_ship_to_num}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {a.is_primary && (
                              <Chip
                                label={t('cart.primary')}
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          {(a.full_address || locality) && (
                            <Typography variant="caption" color="text.secondary">
                              {a.full_address || locality}
                            </Typography>
                          )}
                        </Box>
                      );
                    }}
                  >
                    {shipToAddresses.map((address) => {
                      // Build a "details" line out of whatever address fields
                      // are populated. Epicor often syncs only some columns,
                      // so falling back to address1/city/state separately
                      // avoids ending up with a blank secondary line.
                      const localityParts = [address.city, address.state, address.zip_code]
                        .filter((p) => p && p.trim())
                        .join(', ');
                      const detailLine =
                        address.full_address && address.full_address.trim()
                          ? address.full_address
                          : localityParts;
                      return (
                        <MenuItem
                          key={address.id}
                          value={address.id}
                          sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 1.25 }}
                        >
                          <Box sx={{ width: '100%' }}>
                            {/* Header: name + #ship-to code (the actual
                                differentiator) + primary badge. The code
                                chip is critical — multiple ship-tos often
                                share the same `name`, e.g. "Abadi Baru,TK". */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle2" fontWeight="medium">
                                {address.name}
                              </Typography>
                              <Chip
                                label={`#${address.epicor_ship_to_num}`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {address.is_primary && (
                                <Chip
                                  label={t('cart.primary')}
                                  size="small"
                                  color="primary"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            {detailLine ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}
                              >
                                {detailLine}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                Address details unavailable — confirm with sales before ordering
                              </Typography>
                            )}
                            {(address.contact_name || address.phone) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {[address.contact_name, address.phone].filter(Boolean).join(' • ')}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('cart.orderNotes')}
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('cart.orderNotesPlaceholder')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCheckout} disabled={isProcessing}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateOrder}
            disabled={isProcessing || !selectedShipToId || loadingAddresses}
            startIcon={<CheckoutIcon />}
          >
            {isProcessing ? 'Creating Order...' : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Success Dialog */}
      <Dialog
        open={orderSuccess}
        onClose={handleCloseSuccess}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('cart.orderPlaced')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('cart.orderPlacedDetail')}
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('cart.orderNumberLabel')} <strong>#{orderNumber}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cart.orderConfirmEmail')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccess} variant="contained">
            {t('cart.continueShopping')}
          </Button>
        </DialogActions>
      </Dialog>

      <CreditHoldDialog
        open={creditHoldDialogOpen}
        onClose={() => setCreditHoldDialogOpen(false)}
      />
    </Box>
  );
};

export default CartPage;